import { BsCalendar, BsPen } from 'react-icons/bs'
import { FaGoogle, FaNotesMedical, FaProcedures, FaTiktok } from 'react-icons/fa'
import { FaHandshake, FaPeopleGroup, FaUserDoctor } from 'react-icons/fa6'
import { IoPeopleSharp } from 'react-icons/io5'
import {
    MdAccountBalance,
    MdAccountTree,
    MdAdminPanelSettings,
    MdApartment,
    MdArticle,
    MdHowToReg,
    MdBadge,
    MdAssignment,
    MdAttachMoney,
    MdAutoAwesome,
    MdBedroomParent,
    MdBiotech,
    MdBusiness,
    MdCampaign,
    MdAdsClick,
    MdCalculate,
    MdCategory,
    MdCleaningServices,
    MdConfirmationNumber,
    MdDashboard,
    MdDescription,
    MdDraw,
    MdEditNote,
    MdEmojiEvents,
    MdExitToApp,
    MdFilterList,
    MdFolder,
    MdBookmark,
    MdBrandingWatermark,
    MdFlashOn,
    MdHandshake,
    MdHealthAndSafety,
    MdHistory,
    MdInventory,
    MdInventory2,
    MdListAlt,
    MdLink,
    MdLocalHospital,
    MdLocalOffer,
    MdLocalPharmacy,
    MdLocalShipping,
    MdLock,
    MdMedicalServices,
    MdMedication,
    MdMeetingRoom,
    MdMenuBook,
    MdMonitorHeart,
    MdOutlineSupportAgent,
    MdPeople,
    MdPointOfSale,
    MdLocalAtm,
    MdReceipt,
    MdScience,
    MdSecurity,
    MdSell,
    MdSettings,
    MdVideoCall,
    MdVpnKey,
    MdBarChart,
    MdSettingsInputComposite,
    MdViewCarousel,
    MdStorefront,
    MdSwapHoriz,
    MdList,
    MdCheckCircle,
    MdVerifiedUser,
    MdAccountBalanceWallet,
    MdThermostat,
    MdAccessTime,
    MdTimer,
    MdSend,
    MdVerified,
    MdVideocam,
    MdWallet,
    MdWaterDrop,
    MdWhatsapp,
    MdBolt,
    MdWidgets,
    MdMarkEmailRead,
    MdPalette,
    MdForum,
} from 'react-icons/md'
import { PiUsersBold } from 'react-icons/pi'

export interface SidebarResourceConfig {
    icon: any
    href: string
}

export interface SidebarModuleConfig {
    icon: any
    resources: Record<string, SidebarResourceConfig>
}

export const HIDE_RESOURCE_BY_SECONDARY_SEGMENT: Record<string, string[]> = {
    INTERMEDIATION: [
        'prescriptions',
        'triage',
        'certificates',
        'digital_certificates',
        'teleconsultations',
        'teleconsultation',
    ],
    OTHER: [],
}

/**
 * Módulos que não existem para conta de pessoa física.
 *
 * Emitir nota exige inscrição no fisco com CNPJ (NF-e pressupõe inscrição estadual
 * de contribuinte de ICMS) e a integração é ancorada em CNPJ de ponta a ponta. MEI
 * é pessoa jurídica e continua vendo o módulo.
 *
 * O certificado digital NÃO entra aqui: existe e-CPF, e a tela também serve à
 * assinatura digital de documentos médicos.
 */
export const HIDE_MODULES_FOR_INDIVIDUAL: string[] = ['fiscal']

export const SIDEBAR_MODULE_CONFIG: Record<string, SidebarModuleConfig> = {
    dashboard: {
        icon: MdDashboard,
        resources: {
            view: { icon: MdDashboard, href: '/dashboard' },
        },
    },
    person: {
        icon: IoPeopleSharp,
        resources: {
            clients: { icon: FaPeopleGroup, href: '/person/clients' },
            professionals: {
                icon: FaUserDoctor,
                href: '/person/professionals',
            },
            partners: { icon: FaHandshake, href: '/person/partners' },
            suppliers: { icon: MdLocalShipping, href: '/person/suppliers' },
        },
    },
    commerce: {
        icon: MdStorefront,
        resources: {
            // Mercadoria é item de catálogo desde a unificação: o menu leva à listagem do
            // catálogo já filtrada. A permissão continua sendo `commerce.products`.
            products: {
                icon: MdMenuBook,
                href: '/registrations/catalog?category=PRODUCT',
            },
            // Venda de balcão: mercadoria vendida no painel, sem depender da loja.
            counter_sales: {
                icon: MdPointOfSale,
                href: '/commerce/counter-sale',
            },
            'store-config': { icon: MdStorefront, href: '/commerce/stores' },
            // Editor de tema da vitrine: cada loja escolhe e personaliza o seu.
            'store-themes': { icon: MdPalette, href: '/commerce/themes' },
            'store-widgets': {
                icon: MdWidgets,
                href: '/commerce/stores/widgets',
            },
            // Inscritos da newsletter do rodapé da loja.
            'store-newsletter': { icon: MdMarkEmailRead, href: '/commerce/newsletter' },
            product_categories: {
                icon: MdCategory,
                href: '/registrations/product-categories',
            },
            promos: { icon: MdLocalOffer, href: '/commerce/promos' },
            coupons: { icon: MdConfirmationNumber, href: '/commerce/coupons' },
        },
    },
    atendimento: {
        icon: MdOutlineSupportAgent,
        resources: {
            agenda: { icon: BsCalendar, href: '/atendimento/agenda' },
            channels: { icon: MdWhatsapp, href: '/atendimento/chats' },
            sectors: { icon: MdBusiness, href: '/atendimento/sectors' },
            agents: { icon: MdAutoAwesome, href: '/atendimento/agents' },
            conversation_simulator: { icon: MdForum, href: '/atendimento/simulator' },
            integrations: {
                icon: MdSettingsInputComposite,
                href: '/integrations',
            },
        },
    },
    // Núcleo agendável, agnóstico de segmento (clínica, salão, academia, pet, oficina).
    // Ícone e vocabulário aqui são os da SAÚDE (a base instalada tem tutoriais e prints
    // com eles); quem traduz para os outros segmentos é `segment-labels.ts`.
    bookings: {
        icon: MdLocalHospital,
        resources: {
            scheduling: { icon: BsCalendar, href: '/atendimento/scheduling' },
            consultations: {
                icon: FaNotesMedical,
                href: '/bookings/consultations',
            },
            teleconsultations: {
                icon: MdVideocam,
                href: '/bookings/teleconsultations',
            },
            exams: { icon: MdBiotech, href: '/bookings/exams' },
            orders: { icon: FaProcedures, href: '/bookings/orders' },
            triage: { icon: MdAssignment, href: '/bookings/triage' },
            packages: { icon: MdInventory2, href: '/bookings/packages' },
            reports: { icon: MdArticle, href: '/bookings/reports' },
            presence: { icon: MdHowToReg, href: '/bookings/presence' },
            settings: { icon: MdSettings, href: '/bookings/settings' },
        },
    },
    // ⚠️ `medical` NÃO tem entrada aqui, e é o que faz o módulo inteiro sumir do menu
    // (`admin-sidebar` descarta módulo sem config).
    //
    // Receituário, atestado e prontuário são o MESMO documento visto de ângulos
    // diferentes, e nenhum deles existe sem um paciente: ninguém abre "receituário"
    // sem saber de quem. Todos os três se alcançam pelo prontuário do paciente, em
    // /person/clients/:id/prontuario — receituário e atestados são abas de lá
    // (`prontuario-tabs.ts`). Uma listagem geral de receitas da empresa era simetria
    // com telas de cadastro, não necessidade: evolução, anamnese e diagnóstico nunca
    // tiveram uma e ninguém sentiu falta.
    //
    // O módulo continua existindo como PERMISSÃO (Receituário, Atestados, Acesso ao
    // Prontuário) e aparece normalmente na tela de perfis de acesso — some só do menu
    // lateral. As rotas /medical/prescriptions/* seguem vivas: a aba do prontuário
    // navega para `/new` e `/:id`, e links salvos continuam abrindo.
    registrations: {
        icon: BsPen,
        resources: {
            catalog: { icon: MdListAlt, href: '/registrations/catalog' },
            catalog_types: {
                icon: MdCategory,
                href: '/registrations/catalog/types',
            },
            specialties: {
                icon: MdMedicalServices,
                href: '/registrations/specialties',
            },
            rooms: { icon: MdMeetingRoom, href: '/registrations/rooms' },
            health_plans: {
                icon: MdHealthAndSafety,
                href: '/registrations/convenios',
            },
            medical_supplies: {
                icon: MdInventory2,
                href: '/registrations/supplies',
            },
            supplier_types: {
                icon: MdCategory,
                href: '/registrations/supplier-types',
            },
            medical_services: {
                icon: MdAssignment,
                href: '/registrations/medical-services',
            },
            establishments: {
                icon: MdBusiness,
                href: '/registrations/establishments',
            },
            digital_certificates: {
                icon: MdVerified,
                href: '/registrations/certificates',
            },
            templates: {
                icon: MdDescription,
                href: '/registrations/templates',
            },
            letterheads: {
                icon: MdBrandingWatermark,
                href: '/registrations/letterheads',
            },
        },
    },
    health_integrations: {
        icon: MdHealthAndSafety,
        resources: {
            overview: { icon: MdHealthAndSafety, href: '/health-integrations' },
            lab: { icon: MdScience, href: '/health-integrations/lab' },
            tiss: {
                icon: MdMedicalServices,
                href: '/health-integrations/tiss',
            },
            pharmacy: {
                icon: MdLocalPharmacy,
                href: '/health-integrations/pharmacy',
            },
            pacs: { icon: MdBiotech, href: '/health-integrations/pacs' },
            rnds: {
                icon: MdHealthAndSafety,
                href: '/health-integrations/rnds',
            },
        },
    },
    stock: {
        icon: MdInventory2,
        resources: {
            dashboard: { icon: MdInventory2, href: '/stock' },
            medications: { icon: MdMedication, href: '/stock/medications' },
            batches: { icon: MdInventory, href: '/stock/batches' },
            temperature: { icon: MdThermostat, href: '/stock/temperature' },
            sngpc: { icon: MdSecurity, href: '/stock/sngpc' },
            dispensation: {
                icon: MdLocalPharmacy,
                href: '/stock/dispensation',
            },
        },
    },
    hospital: {
        icon: MdLocalHospital,
        resources: {
            dashboard: { icon: MdDashboard, href: '/hospital' },
            hospitals: {
                icon: MdLocalHospital,
                href: '/registrations/hospitals',
            },
            units: { icon: MdApartment, href: '/hospital/unidades' },
            beds: { icon: MdBedroomParent, href: '/hospital/beds' },
            admissions: { icon: MdLocalHospital, href: '/hospital/admissions' },
            cleaning: { icon: MdCleaningServices, href: '/hospital/cleaning' },
            transfers: { icon: MdSwapHoriz, href: '/hospital/transfers' },
            vital_signs: {
                icon: MdMonitorHeart,
                href: '/hospital/vital-signs',
            },
            fluid_balance: {
                icon: MdWaterDrop,
                href: '/hospital/fluid-balance',
            },
            nursing_evolution: {
                icon: MdEditNote,
                href: '/hospital/evolucoes',
            },
            prescriptions: {
                icon: MdMedication,
                href: '/hospital/prescricoes',
            },
            discharge: { icon: MdExitToApp, href: '/hospital/discharges' },
        },
    },
    financial: {
        icon: MdAttachMoney,
        resources: {
            dashboard: { icon: MdDashboard, href: '/financial/dashboard' },
            billings: { icon: MdReceipt, href: '/financial/billings' },
            cash_register: {
                icon: MdLocalAtm,
                href: '/financial/cash-register',
            },
            sales_simulator: {
                icon: MdCalculate,
                href: '/financial/sale-simulator',
            },
            billing_approvals: {
                icon: MdCheckCircle,
                href: '/financial/bills-approvals',
            },
            payment_account: {
                icon: MdAccountBalanceWallet,
                href: '/financial/digital-account',
            },
            ledger: { icon: MdList, href: '/financial/statement' },
            transfers: { icon: MdSwapHoriz, href: '/financial/transfers' },
            transfer_approvals: {
                icon: MdVerifiedUser,
                href: '/financial/transfers-approvals',
            },
            terminals: { icon: MdPointOfSale, href: '/financial/terminals' },
            chart_of_accounts: {
                icon: MdAccountTree,
                href: '/financial/chart-of-accounts',
            },
            accounting_dashboard: {
                icon: MdBookmark,
                href: '/financial/accounting',
            },
            journal_entries: {
                icon: MdBookmark,
                href: '/financial/journal-entries',
            },
            accounting_rules: {
                icon: MdFilterList,
                href: '/financial/accounting-rules',
            },
            financial_categories: {
                icon: MdCategory,
                href: '/financial/financial-categories',
            },
            cost_centers: { icon: MdBusiness, href: '/financial/cost-centers' },
            bank_accounts: {
                icon: MdAccountBalance,
                href: '/financial/bank-accounts',
            },
            settings: { icon: MdSettings, href: '/financial/settings' },
        },
    },
    billing: {
        icon: MdReceipt,
        resources: {
            guides: { icon: MdReceipt, href: '/billing/guides' },
            authorizations: {
                icon: MdAssignment,
                href: '/billing/authorizations',
            },
            batches: { icon: MdInventory, href: '/billing/batches' },
            price_tables: { icon: MdListAlt, href: '/billing/price-tables' },
            coparticipation: {
                icon: MdAttachMoney,
                href: '/billing/coparticipation',
            },
            glosses: { icon: MdDescription, href: '/billing/glosses' },
        },
    },
    administrative: {
        icon: MdDescription,
        resources: {
            units: { icon: MdApartment, href: '/person/companies' },
            reports: { icon: MdAssignment, href: '/core/reports' },
            contracts: {
                icon: MdDescription,
                href: '/administrative/contracts',
            },
            sign: { icon: MdDraw, href: '/commerce/subscriptions' },
            resale: { icon: MdStorefront, href: '/administrative/resales' },
            //inbox: { icon: MdEmail, href: '/administrative/inbox' },
        },
    },
    fiscal: {
        icon: MdReceipt,
        resources: {
            invoices: { icon: MdReceipt, href: '/fiscal/invoices' },
            settings: { icon: MdSettings, href: '/fiscal/settings' },
        },
    },
    marketing: {
        icon: MdCampaign,
        resources: {
            dashboard: { icon: MdDashboard, href: '/marketing/dashboard' },
            campaigns: { icon: MdCampaign, href: '/marketing/campaigns' },
            meta: { icon: MdAdsClick, href: '/marketing/meta' },
            google_ads: { icon: FaGoogle, href: '/marketing/google-ads' },
            site_banners: {
                icon: MdViewCarousel,
                href: '/marketing/site-banners',
            },
            events: { icon: MdBolt, href: '/marketing/events' },
            tiktok_ads: { icon: FaTiktok, href: '/marketing/tiktok-ads' },
        },
    },
    messaging: {
        icon: MdSend,
        resources: {
            whatsapp_templates: {
                icon: MdWhatsapp,
                href: '/messaging/whatsapp-templates',
            },
            quick_messages: {
                icon: MdBolt,
                href: '/messaging/quick-messages',
            },
            whatsapp_account: {
                icon: MdLink,
                href: '/messaging/whatsapp-account',
            },
            settings: {
                icon: MdSettings,
                href: '/messaging/settings',
            },
            whatsapp_broadcasts: {
                icon: MdCampaign,
                href: '/messaging/whatsapp-broadcasts',
            },
        },
    },
    security: {
        icon: MdLock,
        resources: {
            employees: { icon: PiUsersBold, href: '/security/users' },
            permissions: {
                icon: MdAdminPanelSettings,
                href: '/security/permissions',
            },
            events: { icon: MdHistory, href: '/security/events' },
        },
    },
    sales: {
        icon: MdSell,
        resources: {
            dashboard: { icon: MdDashboard, href: '/sales' },
            pipelines: { icon: MdFilterList, href: '/sales/pipelines' },
            leads: { icon: MdPeople, href: '/sales/leads' },
            deals: { icon: MdHandshake, href: '/sales/deals' },
            wallets: { icon: MdWallet, href: '/sales/wallets' },
            slas: { icon: MdTimer, href: '/sales/slas' },
            automations: { icon: MdFlashOn, href: '/sales/automations' },
            gamification: { icon: MdEmojiEvents, href: '/sales/gamification' },
            config: { icon: MdSettings, href: '/sales/settings' },
        },
    },
    storage: {
        icon: MdFolder,
        resources: {
            files: { icon: MdFolder, href: '/storage/files' },
            shares: { icon: MdLink, href: '/storage/shares' },
            settings: { icon: MdSettings, href: '/storage/settings' },
        },
    },
    video: {
        icon: MdVideoCall,
        resources: {
            rooms: { icon: MdVideoCall, href: '/video/rooms' },
            api: { icon: MdVpnKey, href: '/video/api-keys' },
            usage: { icon: MdBarChart, href: '/video/usage' },
            settings: { icon: MdSettings, href: '/video/settings' },
        },
    },
    hr: {
        icon: MdBadge,
        resources: {
            employees: { icon: MdPeople, href: '/hr/funcionarios' },
            payroll: { icon: MdReceipt, href: '/hr/payroll' },
            rubrics: { icon: MdListAlt, href: '/hr/rubrics' },
            payroll_tables: { icon: MdCategory, href: '/hr/tabelas' },
            vacations: { icon: BsCalendar, href: '/hr/vacations' },
            time_tracking: { icon: MdTimer, href: '/hr/time-tracking' },
            esocial: { icon: MdSend, href: '/hr/esocial' },
            hour_bank: { icon: MdAccessTime, href: '/hr/hour-bank' },
        },
    },
}
